import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../../lib/api";
import { connectionsApi, shownState, type Connection } from "../../lib/connectionsApi";
import { useOnShown } from "../../lib/inApp";

const POLL_MS = 4000;

/** A product's connections, read again while any check is running, and on demand. */
export function useConnections(productId: string | null) {
  const [conns, setConns] = useState<Connection[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const current = useRef(productId);
  current.current = productId;

  const reload = useCallback(async () => {
    if (!productId) return;
    try {
      const r = await connectionsApi.list(productId);
      // Why: a slow answer for the product the owner just left must not show under the new one.
      if (current.current !== productId) return;
      setConns(r.connections);
      setError(null);
    } catch (e) {
      if (current.current === productId) setError(e instanceof ApiError ? e.message : "The connections did not load. Try again.");
    }
  }, [productId]);

  useEffect(() => {
    setConns(null);
    void reload();
  }, [reload]);
  // Why: inside the phone app a Meta sign-in runs in the phone's browser, so its end shows when the owner
  // comes back to this screen; any other change made meanwhile shows too.
  useOnShown(() => void reload());

  const checking = !!conns?.some((c) => shownState(c) === "checking");
  useEffect(() => {
    if (!checking) return;
    const t = setInterval(() => {
      if (document.visibilityState === "visible") void reload();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [checking, reload]);

  return { conns, error, reload };
}
